import { Injectable, NgZone } from '@angular/core';

import { HttpClient, HttpHeaders } from '@angular/common/http';
import { deepClone, defaults, emailToName, isFamilyAllExpired } from '../utils';

import { MechanicsService } from "./mechanics.service"

import { User } from '../interfaces/user.interface';
import { Company } from '../interfaces/company.interface';
import { Patent } from '../interfaces/patent.interface';
import { Family, Modified } from '../interfaces/family.interface';
import { MedicineSet } from '../interfaces/medicineSet.interface';
import { Agency } from '../interfaces/agency.interface';
import { Toast, ToastConfig } from '../interfaces/toast.interface';


import { ToastrService } from 'ngx-toastr';
import { lastValueFrom, Observable } from 'rxjs';


import { environment } from "../../environments/environment"

export { supportedCountries, countryGroups, countryGroupKeys } from '../utils';

export const ignoredCountries: string[] = ["VN"]; // Too problematic to resolve;

declare const moment: any

const appWindow = window as Window & {
	urlPrefix?: string;
	user?: User;
};

@Injectable()
export class UtilsService {

	public dateFormatter!: Intl.DateTimeFormat; // using the native window.Intl.DateTimeFormat. Init in constructor. Use with: const formattedDate:string = dateFormatter.format(date:Date);

	// Same. Use with: const formattedDate:string = dateFormatterHuman.format(date:Date);
	public dateFormatterISO = new Intl.DateTimeFormat("en-CA",
		{ // "2022-02-01"
			year: "numeric",
			month: "numeric",
			day: "numeric"
		});

	constructor(public http: HttpClient,
		public ms: MechanicsService,
		public toastr: ToastrService,
		private ngZone: NgZone) {

		this.initDateFormatter()
	}

	getOneFamily({ _id, cc, fullFamily = false, user = null }: { _id: string, cc?: string[], fullFamily?: boolean, user?: User | null }): Observable<any> { // Returns an encoded string (NODE_ENV==="production") or plain JSON

		/*
			I placed getOneFamily in utils.service, because:
			- it needs computeFamilyCustomKeys in this same service, so I can't put it in ms
			- I can't put it in mas, because the public search page does not load mas
			- cc is actually ss.selectedCountries, but since I can't inject ss inside ms or utils, I am passing it through an argument
		*/

		const l = `utils.getOneFamily() - `

		let headerObj: Record<string, string> = {}

		if (user) {
			headerObj = { 'Authorization': 'Bearer ' + user!.token?.value }
		}

		let headers = new HttpHeaders(headerObj);

		this.ms.isLoading = true

		let conditions: { _id: string, cc?: string[], fullFamily?: boolean } = { _id, cc };

		if (fullFamily) {
			// Don't add { fullFamily:false } because this key will be visible in the devtools and I don't want people to use it. Add it only when it's true
			conditions["fullFamily"] = true;
		}

		return this.http.post(
			`${environment.apiUrl}${appWindow.urlPrefix}/api/getOneFamily`,
			conditions,
			{
				headers,
				responseType: "text"
			} as any); // "as any" otherwise Typescript says "Type 'string' is not assignable to type '"json"', WTF

	}

	async sendMailToCompany(user: User) { // Since the UserService cannot be imported in the UtilsService, I pass the User from the component


		if (!this.ms.selectedFamily) { // normally impossible
			// console.log(`buildAssociationsEditor() - No currently selected family`)
			return
		}

		this.ms.isLoading = true

		let headers = new HttpHeaders({ 'Authorization': 'Bearer ' + (appWindow.user?.token?.value || '') })

		// console.log(`Sending mail to `, this.ms.selectedFamily._company);

		const contactCompanyForm = this.ms.contactCompanyForm as typeof this.ms.contactCompanyForm & { companyID?: string };
		contactCompanyForm.companyID = (this.ms.selectedFamily._company as Company)._id

		try {
			await lastValueFrom(this.http.post(`${environment.apiUrl}${appWindow.urlPrefix}/api/sendMailToCompany`, contactCompanyForm, { headers }));

			this.ms.isLoading = false
			this.toast({ severity: 'success', detail: `Email sent to ${(this.ms.selectedFamily._company as Company).displayedName}` }, { timeOut: 5000 })

			this.ms.contactCompanyForm = {
				message1: "",
				queryType: "",
				message2: "",
				thirdParty: "",
				thirdPartyName: ""
			}

			this.ms.display["modalContactCompany"] = false
		} catch (err) {
			this.ms.isLoading = false
			console.error(err);
			this.toast({ severity: 'error', detail: 'Something went wrong... Not sure what' }, { timeOut: 3000 })

		}
	}

	async sendTestMail(payload: any) {

		const l = `sendTestMail() - `

		this.ms.isLoading = true

		let headers = new HttpHeaders({ 'Authorization': 'Bearer ' + (appWindow.user?.token?.value || '') })

		// console.log(`Sending mail with payload = `, payload);

		try {
			await lastValueFrom(this.http.post(`${environment.apiUrl}${appWindow.urlPrefix}/api/sendTestMail`, payload, { headers }));

			this.ms.isLoading = false;

			this.toast({ severity: 'success', detail: `Email sent to ${payload.recipient}` }, { timeOut: 5000 });

		} catch (err) {

			this.ms.isLoading = false
			console.error(err);
			this.toast({ severity: 'error', detail: 'Something went wrong... Not sure what' }, { timeOut: 3000 })
		}
	}

	async sendMailInaccuracies(): Promise<void> {

		const l = `utilsService sendMailInaccuracies() - `

		this.ms.isLoading = true

		try {

			await lastValueFrom(this.http.post(`${environment.apiUrl}${appWindow.urlPrefix}/api/reportInaccuracies`, this.ms.reportInaccuraciesForm));

			this.ms.isLoading = false
			this.toast({ severity: 'success', detail: `Message sent. Thank you!` }, { timeOut: 5000 })

			this.ms.reportInaccuraciesForm = {
				from: "",
				familyId: "",
				message: "",
				companyId: ""
			}

			this.ms.closeCurrentModal(l)

		} catch (err) {
			this.ms.isLoading = false
			console.error(err);
			this.toast({ severity: 'error', detail: 'Something went wrong... Not sure what' }, { timeOut: 3000 })
		}
	}

	logoUrl(company: Company): string {

		const l = `utils.logoUrl() - `

		// console.log(`${l}Working out logo URL for company=`, company)

		if (!company || !company.uid) return "";

		return this.ms.urlPrefix + '/assets/images/logos/' + company.uid + '.svg'
	}

	toast(toast: Toast, config: ToastConfig = {}) {

		const l = `utils.service toast() - `

		config = Object.assign({
			timeOut: 1500,
			enableHtml: true
		}, config);

		// Convert detail to string if it's an object
		let detail = toast.detail;
		if (detail && typeof detail === 'object') {
			detail = JSON.stringify(detail, null, 2);
		} else if (detail === undefined || detail === null) {
			detail = 'An error occurred';
		}

		console.log(`${l}toast = `, toast, "config=", config);
		console.log(`${l}toastr service = `, this.toastr);

		// Wrap in NgZone.run() to ensure change detection is triggered in Angular 20
		this.ngZone.run(() => {
			if (!toast.severity) return;
			const toastMethod = toast.severity as "success" | "error" | "info" | "warning";
			this.toastr[toastMethod](detail, toast.title, config);
		});

	}

	initDateFormatter(format?: string): void {

		const l = `ms.initDateFormatter - `

		// console.log(`${l}passed format = '${format}'`)

		// Default - International / ISO 8601
		let locale: string = "en-CA",
			formatObj: Intl.DateTimeFormatOptions = {
				"year": "numeric",
				"month": "numeric",
				"day": "numeric"
			};

		switch (format) {

			case "M/D/YYYY": // American
				locale = "en-US"
				break;

			case "DD/MM/YYYY": // European
				locale = "fr-FR";
				break;

			case "month DD, YYYY": // European
				locale = "en-US";
				formatObj = { // May 17, 2022
					"year": "numeric",
					"month": "short",
					"day": "numeric"
				}
				break;

			// Default: ISO
		}

		// console.log(`${l}Initializing date formatter with locale='${locale}'`)

		this.dateFormatter = new Intl.DateTimeFormat(locale, formatObj)// If this was in utils.ts, the locale won't update after init. For the locale to be dynamic, this needs to be in a service
	}

	formatDate(isoDateString?: string, doAddTime: boolean = false): string {

		const l = `utils.formatDate - `

		// Input: an ISO date ('2022-05-17T11:47:32.264Z'  or  "2022-05-17")
		// Output: formatted date according to user's preferences
		// console.log(`${l}this = `, this)

		// console.log(`${l}Received isoDateString to parse = `, isoDateString);

		try {

			const date = isoDateString ? new Date(isoDateString) : new Date();

			let toReturn = this.dateFormatter.format(date);

			if (doAddTime) { // Adding hour of the day

				let format = "HH:mm"

				if (appWindow.user?.preferences?.timeFormat === "AM/PM") {
					format = "h:mma"; // 8:52pm
				}

				// console.log(`${l}moment(${date}).format(${format}) = ${moment(date).format(format)}`)

				toReturn += " " + moment(date).format(format);
			}

			return toReturn

		} catch (err) {

			if (isoDateString && /^\d{4}-\d-/.test(isoDateString)) {
				// Safari bug: Apparently, Safary finds it very clever to transform "1998-06-06" to "1998-6-6"

				isoDateString = isoDateString
					.split("-")
					.map(n => /^\d$/.test(n) ? `0${n}` : n)
					.join("-");

				return this.formatDate(isoDateString)
			}

			// console.warn(`${l}Caught error : `, err);
			return "?";
		}
	}

	computeMedicineSetCustomKeys(med: MedicineSet): MedicineSet { // Used in both the Manager and the Search services, so I put it in the utils

		const l = `computeMedicineSetCustomKeys() - `
		// Making some human readable output out of objects

		// console.log(`${l}instanceof UtilsService ?`, this instanceof UtilsService)

		/* Custom keys:

		  families?: {
				internalNumber: string;
				patents:[{
					grantNum: string;
					cc: string
				}]
				title: string;
				_id: string;
				_score: number;
		  }[];
		  relevancy?: number
		*/

		// console.log(`Computing families for MedSet `, med);

		if (med.modified) {
			med.modified.name = emailToName(med.modified.by)
			med.modified.dateMoment = this.formatDate(med.modified.date, true)
		}

		med.families = (med.families || []).map(f => this.computeFamilyCustomKeys(f));

		// console.log(`${l} Final MedicineSet = `, med)

		return med
	}

	computeLogCustomKeys(log: any): any {

		const l = `computeLogCustomKeys() - `

		log.dateMoment = this.formatDate(log.date, true)

		return log
	}

	computeFamilyCustomKeys(f: Family): Family { // filterOutPatents when opening the full family in the export const search results

		const l = `utils.computeFamilyCustomKeys - `
		const streamedStats = f.patentsTotal !== undefined ? f.stats : undefined;
		const streamedWarnings = f.patentsTotal !== undefined ? f.warnings : undefined;

		// console.log(`computeFamilyCustomKeys - f = `, f);

		if (typeof (f) === "string") {
			console.error(`327 ${l}Caught error: family is a string!`);
			return f
		}

		try {
			f.displayedTitle = f.displayedTitle || (f.title && f.title[0] && f.title[0]["content"]) || "(Title not available)";
			// console.log(`${l}Passed family=`, f)
		} catch (err) {
			console.error(`${l}Caught error - Crashed while attempting to do 'f.displayedTitle = f.displayedTitle || f.title[0] && f.title[0]["content"]' - passed family =`, f)
			f.displayedTitle = "(Title not available)"
		}


		if (!f.patents || !f._medicineSets) {
			// It's very likely a family passed by socket, a minimalist family with only _id and title, just to update the column on the left. No need for custom keys
			// console.log(`${l}Family has no patents or medicineSets. Returning it as is.`);
			return f
		}

		// console.log(`${l}patents before custom keys = `, structuredClone(f.patents));

		f.patents = f.patents.map(patent => this.computePatentCustomKeys(patent));

		// console.log(`${l}patents after custom keys = `, structuredClone(f.patents));

		const reals: Patent[] = f.patents.filter(p => !p.isVirtual);

		const patentsList: Patent[] = ([0, 5].includes(appWindow.user?.role ?? -1)) ? f.patents : reals;

		// console.log(`${l}window["user"].role=${window["user"].role}, all patents length=${f.patents.length}, patentsList.length=${patentsList.length}`);


		f.stats = { // Pfizer 2019-09-19
			patents: {
				virtuals: f.patents.filter(p => p.isVirtual).length,
				reals: reals.length,
				total: f.patents.length,

				expired: patentsList.filter(p => p.isExpired === true).length,
				ongoing: patentsList.filter(p => p.isExpired === false).length,
				expiryUnknown: patentsList.filter(p => p.isExpired === null).length,

				countries: [...new Set(patentsList.filter(p => !p.isVirtual).map(p => p.cc))].length,

				approved: patentsList.filter(p => p.isPscopeApproved).length,
				unapproved: patentsList.filter(p => !p.isPscopeApproved && (p.pscope || "").length > 1).length,

				withLink: patentsList.filter(p => (p.pscope || "").length > 1).length,
				withoutLink: patentsList.filter(p => p.pscope?.length === 1).length,

				crosscheckFailed: patentsList.filter(p => p.WOcrosscheck !== undefined && p.WOcrosscheck < 10).length,
				crosscheckPassed: patentsList.filter(p => p.WOcrosscheck !== undefined && p.WOcrosscheck >= 10 && p.WOcrosscheck < 20).length,
				crosscheckUnknown: patentsList.filter(p => p.WOcrosscheck === undefined).length,
				crosscheckShouldNotSeeMe: patentsList.filter(p => p.WOcrosscheck === 9 && (p.pscope || "").length > 1).length,
			}
		}

		// console.log(`${l}f.stats = `, f.stats)

		if (f.modified) {

			if (!Array.isArray(f.modified)) { // 2022-04-08 New "modified" history, now an array and not just an object
				f.modified = [f.modified]
			};

			f.modified = f.modified.map(mod => {
				mod.name = emailToName(mod.by || "")
				mod.dateMoment = this.formatDate(mod.date || undefined, true)
				mod.comment = mod.comment || ""
				return mod
			})


			f.lastModified = this.getLastModified(f.modified) || deepClone(defaults.modified);

			// console.log(`${l}Full modification history = `, f.modified)
			// console.log(`${l}Found lastModified = `, f.lastModified)

		} else {
			f.lastModified = deepClone(defaults.modified)
		}

		try {
			(f._company as Company).displayedName = (f._company as Company).displayedName.replace(" (Merck USA)", "")
			if (/j&j/i.test((f._company as Company).uid)) (f._company as Company).uid = "jnj"
		} catch (err) {
			// console.warn(`computeFamilyCustomKeys warning = `, err)
		}

		let warnings: string[] = []

		if (!f.internalNumber || !f.internalNumber.length) {
			warnings.push(`No internal number`)
		}

		if (!f._medicineSets.length) {
			warnings.push(`Not searchable (No INN assoc.)`)
		}

		if (f.patents.filter(p => p.cc === "WO" && !p.isVirtual).length === 0) {
			warnings.push("No WO")
		}

		if (isFamilyAllExpired(f)) {
			warnings.push("Not searchable (all expired)")
		} else if (f.stats.patents.expired) {
			warnings.push(`${f.stats.patents.expired}/${f.stats.patents.reals} expired`)// stats are calculated here above
		}


		f.warnings = warnings.join(", ")

		// Metadata-only manager responses contain authoritative statistics and
		// warnings for all patents. Do not replace them with partial-page values.
		if (streamedStats) f.stats = streamedStats;
		if (streamedWarnings !== undefined) f.warnings = streamedWarnings;

		return f
	}

	computePatentCustomKeys(patent: Patent) {

		const l = `utilsService.computePatentCustomKeys - `

		// console.log(`${l}instanceof UtilsService ?`, this instanceof UtilsService)
		// console.log(`computeFamilyCustomKeys - patent = `, patent);

		patent.freeText = patent.freeText || ""

		if (!patent["dates"]) {
			return patent
		}

		patent["datesMoment"] = this.datesToHuman(patent["dates"])

		// WO patents don't expire
		if (patent.isExpired === undefined || patent.isExpired === null) {
			// console.log(`${l}patent["dates"].expiry='${patent["dates"].expiry}'`)
			patent["isExpired"] = patent["dates"].expiry && moment(patent["dates"].expiry).isBefore();

			// if (patent["isExpired"] === null) patent["isExpired"] = false; --> Edit: NO, I want to keep it null! So I have 3 states : true, false and unknown
		}

		// console.log(`${l}'${patent.cc}' is expired = '${patent.isExpired}'`)

		patent["extended"] = !patent["isExpired"] // A patent can't benefit from extended exclusivity if it's isExpired.
			&& (moment().diff(patent["dates"].filing, 'years') >= 20)

		return patent
	}

	dateToHuman(date: string, fromNow: Boolean = false, shift: Number = 0): string {

		if (!date) return "?"

		// date === "2011-10-13T22:00:00.000Z"

		let d = moment(date, "YYYY-MM-DD")

		if (!d.isValid()) return "?"

		if (shift) d = d.add(shift, 'years')

		let fromNowHuman = d.fromNow() // "9 hours ago"
		fromNowHuman = fromNowHuman.includes("hours ago") ? "Today" : fromNowHuman


		const formatted = this.formatDate(date); // Date in "M/D/YYYY" or "DD/MM/YYYY" format, according to the user's preferences


		let output = formatted + (fromNow ? " (" + fromNowHuman.replace(/ /g, "&nbsp;") + ")" : "")


		return output
	}

	datesToHuman(dates: Object): Object {

		const l = `utils.service datesToHuman() - `

		/* dates structure:
		Object {
		  expiry		: null
		  filing		: "2011-10-13T22:00:00.000Z"
		  grant		: null
		  priority	: "2010-10-14T22:00:00.000Z"
		  export constation: "2012-04-18T22:00:00.000Z"
		}
		*/

		if (!dates) {
			console.error(`Received dates is invalid! Skipping`)
			return dates
		}

		const dateValues = dates as Record<string, string | null | undefined>;
		const datesMoment: Record<string, string | null> = {}

		for (let dateKey of Object.keys(dates)) {

			if (!dateValues[dateKey]) continue // null date

			datesMoment[dateKey] = this.dateToHuman(dateValues[dateKey]!, true)

			if (datesMoment[dateKey] === "Invalid Date") datesMoment[dateKey] = null
		}


		return datesMoment
	}

	computeAgencyCustomKeys(agency: Agency): Agency {

		agency.fullAddress = `${agency.street}, ${agency.zip}, ${agency.city}, ${agency.country}`
		agency.approvedHuman = agency.approval?.status === "approved" ? `Yes, by ${agency.approval.by} on ${this.dateToHuman(agency.approval.date, true)}` : "No"
		agency.createdHuman = this.dateToHuman(agency.created || "", true)
		agency.procureMedicinesHuman = agency.procureMedicines ? "Yes" : "No"
		return agency
	}

	getLastModified(modified: Modified[]): Modified {

		const toExclude = ["wipo", "ifpma", "pissis"];

		const modifiedNotByUs = modified.filter(m => !toExclude.some(extension => m.by?.includes(extension)));

		const lastModified: Modified = modifiedNotByUs.length ?
			modifiedNotByUs[0] // If possible, take the latest modification not made by someone from WIPO or IFPMA
			:
			modified[0]; // Fall back to the latest modification, whatever it is

		return lastModified
	}

}

